import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, ImagePlus, KeyRound, Layers, Loader2, LogOut, Pencil, Plus, Trash2, TriangleAlert, X } from "lucide-react";
import { AdminOrders } from "@/components/AdminOrders";
import { AdminPreorders } from "@/components/AdminPreorders";
import { AdminReviews } from "@/components/AdminReviews";
import { AdminSettings } from "@/components/AdminSettings";
import { toast } from "sonner";
import { API, inr, usePageTitle } from "@/context/ShopContext";

const CATEGORIES = ["hijabs", "abaya", "ehram", "bhukoor"];

const EMPTY_PRODUCT = {
  id: "", name: "LAMSAH Hijab", shade: "", colour_family: "Pink", fabric: "", collection: "", colour_desc: "", supplier_note: "",
  length: "", price: 450, stock: 25, description: "", care: "", featured: false, published: true, category: "hijabs",
};

const detailText = (e) => {
  const d = e?.response?.detail || e?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return "Something went wrong.";
};

export default function Admin() {
  usePageTitle("Owner Studio");
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [fabricFilter, setFabricFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkFabric, setBulkFabric] = useState("");
  const [variantFabric, setVariantFabric] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [tab, setTab] = useState("products");
  const [lowOnly, setLowOnly] = useState(false);
  const [catMeta, setCatMeta] = useState({ fabrics: [], families: [], collections: [] });
  const fileRef = useRef(null);
  const uploadTarget = useRef({ index: 0, mode: "replace" });

  const FABRICS = ["", ...catMeta.fabrics];
  const FAMILIES = catMeta.families;
  const COLLECTIONS = catMeta.collections;

  useEffect(() => {
    fetch(`${API}/meta`)
      .then((r) => r.json())
      .then((m) => {
        const fabrics = m.fabrics || [];
        setCatMeta({
          fabrics,
          families: (m.families || []).map((f) => f.name),
          collections: (m.collections || []).map((c) => c.name),
        });
        if (fabrics.length) setVariantFabric((v) => v || fabrics[0]);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    fetch(`${API}/admin/products`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => setProducts(d.products || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : false))
      .then(setUser)
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      setUser(data);
      toast.success("Welcome back");
    } catch (err) {
      setError(detailText(err));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    setUser(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      const isNew = !!editing.isNew;
      const payload = { ...editing };
      delete payload.isNew;
      payload.price = Number(payload.price);
      payload.stock = Number(payload.stock);
      if (payload.collection) payload.collection_slug = payload.collection.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      if (isNew) {
        payload.id = payload.id || payload.shade.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
        payload.arabic_name = "لمسة";
        payload.shade_hex = payload.shade_hex || "#E9DED0";
        payload.images = [`/images/products/${payload.id}.jpg`];
        payload.type = payload.type || "single";
        payload.bundle_size = payload.bundle_size || 0;
      }
      const r = await fetch(isNew ? `${API}/admin/products` : `${API}/admin/products/${editing.id}`, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(isNew ? "Product created" : "Product saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(detailText(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    const r = await fetch(`${API}/admin/products/${id}`, { method: "DELETE", credentials: "include" });
    if (r.ok) {
      toast.success("Product deleted");
      load();
    } else toast.error("Delete failed");
  };

  const upload = async (file) => {
    if (!file || !editing || editing.isNew) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("index", String(uploadTarget.current.index));
      fd.append("mode", uploadTarget.current.mode);
      const r = await fetch(`${API}/admin/products/${editing.id}/image`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(uploadTarget.current.mode === "add" ? "Photograph added" : "Photograph replaced");
      setEditing({ ...editing, images: data.images });
      load();
    } catch (err) {
      toast.error(detailText(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const pickUpload = (index, mode) => {
    uploadTarget.current = { index, mode };
    fileRef.current?.click();
  };

  const removeImage = async (index) => {
    if (!editing || editing.isNew) return;
    if (!window.confirm("Remove this photograph?")) return;
    try {
      const r = await fetch(`${API}/admin/products/${editing.id}/image?index=${index}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success("Photograph removed");
      setEditing({ ...editing, images: data.images });
      load();
    } catch (err) {
      toast.error(detailText(err));
    }
  };

  const applyBulk = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/products/bulk-fabric`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ids: selectedIds, fabric: bulkFabric }),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(bulkFabric ? `${bulkFabric} set on ${data.updated} products` : `Fabric cleared on ${data.updated} products`);
      setSelectedIds([]);
      load();
    } catch (err) {
      toast.error(detailText(err));
    } finally {
      setBusy(false);
    }
  };

  const createVariant = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/products/${editing.id}/variant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ fabric: variantFabric }),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(`Variant created — ${data.shade} — ${data.fabric}`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(detailText(err));
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    if (pw.next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ current_password: pw.current, new_password: pw.next }),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success("Password updated — use it on your next sign in.");
      setPwOpen(false);
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(detailText(err));
    } finally {
      setBusy(false);
    }
  };

  if (user === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="w-6 h-6 animate-spin text-taupe" />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-20" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm bg-white border border-beige p-10">
          <p className="overline-label text-center">Private</p>
          <h1 className="font-serif text-3xl text-center mt-3">LAMSAH Studio</h1>
          <p className="font-arabic text-lg text-taupe text-center mt-1" lang="ar" dir="rtl">لمسة</p>
          <div className="space-y-5 mt-8">
            <input
              data-testid="admin-email-input"
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="input-lux" placeholder="Email" autoComplete="username"
            />
            <input
              data-testid="admin-password-input"
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="input-lux" placeholder="Password" autoComplete="current-password"
            />
            {error && <p className="text-sm text-rose" data-testid="admin-login-error">{error}</p>}
            <button type="submit" data-testid="admin-login-button" disabled={busy} className="btn-primary w-full">
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  const filtered = products
    .filter((p) => `${p.name} ${p.shade} ${p.colour_family} ${p.fabric || ""}`.toLowerCase().includes(search.toLowerCase()))
    .filter((p) => (fabricFilter === "all" ? true : fabricFilter === "unassigned" ? !p.fabric : p.fabric === fabricFilter))
    .filter((p) => !lowOnly || p.stock < 5);

  const lowStock = products.filter((p) => p.stock < 5);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 md:py-14" data-testid="admin-dashboard">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="overline-label mb-2">Owner Studio</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light">Products</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" className="btn-secondary !px-5 !py-3" data-testid="admin-back-home">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Storefront
          </Link>
          <button onClick={() => setPwOpen(true)} data-testid="admin-change-password-button" className="btn-secondary !px-5 !py-3">
            <KeyRound className="w-4 h-4" strokeWidth={1.5} /> Password
          </button>
          <button onClick={logout} data-testid="admin-logout-button" className="btn-secondary !px-5 !py-3">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign Out
          </button>
        </div>
      </div>

      <div className="flex gap-1 mt-10 border-b border-beige">
        {[["products", "Products"], ["orders", "Orders"], ["preorders", "Pre-Orders"], ["reviews", "Reviews"], ["settings", "Settings"]].map(([id, label]) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`px-6 py-3 text-[11px] uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${
              tab === id ? "border-espresso text-espresso" : "border-transparent text-taupe hover:text-espresso"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "orders" && <AdminOrders />}
      {tab === "preorders" && <AdminPreorders />}
      {tab === "reviews" && <AdminReviews />}
      {tab === "settings" && <AdminSettings />}

      {tab === "products" && (
      <>
      {lowStock.length > 0 && (
        <div className="mt-8 border border-rose/40 bg-rose/5 px-5 py-4 flex flex-wrap items-center gap-3" data-testid="low-stock-alert">
          <TriangleAlert className="w-4 h-4 text-rose shrink-0" strokeWidth={1.5} />
          <p className="text-sm text-espresso">
            <span className="font-medium">{lowStock.length}</span> product{lowStock.length > 1 ? "s are" : " is"} running low on stock (below 5 pieces)
          </p>
          <button
            onClick={() => setLowOnly(!lowOnly)}
            data-testid="low-stock-toggle"
            className="ml-auto text-[11px] uppercase tracking-[0.15em] border border-rose/50 text-rose px-3.5 py-2 hover:bg-rose hover:text-ivory transition-colors"
          >
            {lowOnly ? "Show All Products" : "Show Low Stock"}
          </button>
        </div>
      )}
      <div className="flex items-center gap-3 mt-8 flex-wrap">
        <input
          data-testid="admin-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search shade, family, fabric…"
          className="input-lux !w-64"
        />
        <select
          data-testid="admin-fabric-filter"
          value={fabricFilter}
          onChange={(e) => setFabricFilter(e.target.value)}
          className="input-lux !w-auto"
          aria-label="Filter by fabric"
        >
          <option value="all">All Fabrics</option>
          {FABRICS.filter(Boolean).map((f) => <option key={f} value={f}>{f}</option>)}
          <option value="unassigned">Fabric not assigned</option>
        </select>
        <button
          onClick={() => setEditing({ ...EMPTY_PRODUCT, collection: COLLECTIONS[0] || "", isNew: true })}
          data-testid="admin-add-product"
          className="btn-primary !px-5 !py-3"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Add Product
        </button>
        <button
          onClick={() => { setBulkMode(!bulkMode); setSelectedIds([]); }}
          data-testid="admin-bulk-toggle"
          className={`${bulkMode ? "btn-primary" : "btn-secondary"} !px-5 !py-3`}
        >
          <Layers className="w-4 h-4" strokeWidth={1.5} /> {bulkMode ? "Exit Bulk Assign" : "Bulk Assign Fabric"}
        </button>
        <p className="text-xs text-taupe ml-auto">{filtered.length} products</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-10 mt-8">
        {filtered.map((p) => (
          <div key={p.id} className="group" data-testid={`admin-product-${p.id}`}>
            <div className="relative bg-beige aspect-[4/5] overflow-hidden">
              <img src={p.images[0]} alt={p.shade} className="w-full h-full object-cover" loading="lazy" />
              {bulkMode && p.type !== "bundle" && (
                <button
                  onClick={() => setSelectedIds((s) => (s.includes(p.id) ? s.filter((x) => x !== p.id) : [...s, p.id]))}
                  data-testid={`bulk-select-${p.id}`}
                  aria-label={`Select ${p.shade}`}
                  className="absolute inset-0 z-10 bg-espresso/5 hover:bg-espresso/10 transition-colors flex items-start p-3"
                >
                  <span className={`w-6 h-6 border flex items-center justify-center transition-colors ${
                    selectedIds.includes(p.id) ? "bg-espresso border-espresso text-ivory" : "bg-ivory/95 border-espresso/40"
                  }`}>
                    {selectedIds.includes(p.id) && <Check className="w-4 h-4" strokeWidth={2} />}
                  </span>
                </button>
              )}
              {p.featured && (
                <span className="absolute top-2 left-2 bg-espresso text-ivory text-[9px] uppercase tracking-[0.15em] px-2 py-1">Featured</span>
              )}
              {p.published === false && (
                <span className="absolute top-2 right-2 bg-taupe text-ivory text-[9px] uppercase tracking-[0.15em] px-2 py-1" data-testid={`admin-hidden-${p.id}`}>Hidden</span>
              )}
            </div>
            <div className="pt-3">
              <p className="font-serif leading-snug">{p.name}</p>
              <p className="text-[11px] uppercase tracking-[0.15em] text-taupe mt-0.5">
                {p.shade} · {p.colour_family}
              </p>
              <p className="text-xs text-espresso/70 mt-1">
                {inr(p.price)} ·{" "}
                <span data-testid={`admin-stock-${p.id}`} className={p.stock === 0 ? "text-rose font-medium" : p.stock < 5 ? "text-rose" : ""}>
                  {p.stock === 0 ? "Out of stock" : p.stock < 5 ? `Low stock — ${p.stock} left` : `Stock ${p.stock}`}
                </span>
              </p>
              <p className={`text-[11px] mt-1 ${p.type === "bundle" ? "text-taupe" : p.fabric ? "text-espresso/80" : "text-rose"}`} data-testid={`admin-fabric-${p.id}`}>
                {p.type === "bundle" ? "Curated set" : p.fabric || "Fabric not assigned"}
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => setEditing({ ...p })}
                  data-testid={`admin-edit-${p.id}`}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 border border-espresso text-espresso px-3 py-2.5 text-[11px] uppercase tracking-[0.15em] hover:bg-espresso hover:text-ivory transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} /> Edit
                </button>
                <button
                  onClick={() => remove(p.id)}
                  data-testid={`admin-delete-${p.id}`}
                  aria-label={`Delete ${p.shade}`}
                  className="w-11 inline-flex items-center justify-center border border-beige text-taupe hover:text-rose hover:border-rose transition-colors"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      </>
      )}

      {bulkMode && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-espresso text-ivory" data-testid="bulk-bar">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex flex-wrap items-center gap-3">
            <p className="text-sm" data-testid="bulk-count">{selectedIds.length} selected</p>
            <button
              onClick={() => {
                const eligible = filtered.filter((p) => p.type !== "bundle").map((p) => p.id);
                setSelectedIds(selectedIds.length === eligible.length ? [] : eligible);
              }}
              data-testid="bulk-select-all"
              className="text-[11px] uppercase tracking-[0.15em] border border-ivory/40 px-3 py-2 hover:border-ivory transition-colors"
            >
              {selectedIds.length && selectedIds.length === filtered.filter((p) => p.type !== "bundle").length ? "Clear selection" : "Select all shown"}
            </button>
            <select
              data-testid="bulk-fabric-select"
              value={bulkFabric}
              onChange={(e) => setBulkFabric(e.target.value)}
              className="bg-transparent border border-ivory/40 text-ivory text-sm px-3 py-2 ml-auto"
              aria-label="Fabric to assign"
            >
              {FABRICS.map((f) => <option key={f} value={f} className="text-espresso">{f || "Fabric not assigned"}</option>)}
            </select>
            <button
              onClick={applyBulk}
              disabled={busy || !selectedIds.length}
              data-testid="bulk-apply"
              className="bg-ivory text-espresso text-[11px] uppercase tracking-[0.15em] px-5 py-2.5 disabled:opacity-40 hover:bg-white transition-colors"
            >
              {busy ? "Applying…" : "Apply Fabric"}
            </button>
          </div>
        </div>
      )}

      {pwOpen && (
        <div className="fixed inset-0 z-50 bg-espresso/40 flex items-center justify-center px-4" onClick={() => setPwOpen(false)} data-testid="admin-password-overlay">
          <form onSubmit={changePassword} className="bg-ivory w-full max-w-sm p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-password-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl">Change Password</h2>
              <button type="button" onClick={() => setPwOpen(false)} aria-label="Close" data-testid="admin-password-close" className="p-2">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
            <p className="text-[11px] text-taupe leading-relaxed mt-2">
              Your new password is private — it is never shown anywhere on the website.
            </p>
            <div className="space-y-4 mt-6">
              <input
                type="password" required value={pw.current} autoComplete="current-password"
                onChange={(e) => setPw({ ...pw, current: e.target.value })}
                className="input-lux" placeholder="Current password" data-testid="admin-pw-current"
              />
              <input
                type="password" required minLength={8} value={pw.next} autoComplete="new-password"
                onChange={(e) => setPw({ ...pw, next: e.target.value })}
                className="input-lux" placeholder="New password (min 8 characters)" data-testid="admin-pw-new"
              />
              <input
                type="password" required minLength={8} value={pw.confirm} autoComplete="new-password"
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                className="input-lux" placeholder="Confirm new password" data-testid="admin-pw-confirm"
              />
            </div>
            <button type="submit" disabled={busy} data-testid="admin-pw-save" className="btn-primary w-full mt-6">
              {busy ? "Saving…" : "Update Password"}
            </button>
          </form>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-espresso/40 flex items-end sm:items-center justify-center" onClick={() => setEditing(null)} data-testid="admin-editor-overlay">
          <div
            className="bg-ivory w-full max-w-lg max-h-[92vh] overflow-y-auto p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-editor"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl">{editing.isNew ? "New Product" : `Edit — ${editing.shade}`}</h2>
              <button onClick={() => setEditing(null)} aria-label="Close editor" data-testid="admin-editor-close" className="p-2">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            {!editing.isNew && (
              <div className="mt-5">
                <p className="overline-label mb-3">Photographs ({editing.images?.length || 0})</p>
                <input
                  ref={fileRef} type="file" accept="image/*" className="hidden"
                  data-testid="admin-file-input"
                  onChange={(e) => upload(e.target.files?.[0])}
                />
                <div className="grid grid-cols-3 gap-3">
                  {(editing.images || []).map((img, i) => (
                    <div key={`${img}-${i}`} className="group relative">
                      <img src={img} alt="" className="w-full aspect-square object-cover bg-beige" data-testid={`admin-image-${i}`} />
                      <div className="flex gap-1 mt-1.5">
                        <button
                          onClick={() => pickUpload(i, "replace")}
                          disabled={uploading}
                          data-testid={`admin-replace-image-${i}`}
                          className="flex-1 text-[10px] uppercase tracking-[0.12em] border border-espresso/60 py-1.5 hover:bg-espresso hover:text-ivory transition-colors"
                        >
                          Replace
                        </button>
                        {(editing.images || []).length > 1 && (
                          <button
                            onClick={() => removeImage(i)}
                            disabled={uploading}
                            data-testid={`admin-remove-image-${i}`}
                            aria-label="Remove photograph"
                            className="text-[10px] uppercase tracking-[0.12em] border border-rose/60 text-rose px-2 py-1.5 hover:bg-rose hover:text-ivory transition-colors"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => pickUpload(-1, "add")}
                  disabled={uploading}
                  data-testid="admin-add-image"
                  className="btn-secondary !px-5 !py-2.5 mt-4"
                >
                  <ImagePlus className="w-4 h-4" strokeWidth={1.5} />
                  {uploading ? "Uploading…" : "Add Photo"}
                </button>
                <p className="text-[11px] text-taupe mt-2 leading-relaxed">
                  Upload straight from your iPhone. Replace or remove any photo — the first one is the main photo customers see.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mt-6">
              {[
                ["name", "Name"], ["shade", "Shade"],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="overline-label block mb-2">{label}</label>
                  <input className="input-lux" data-testid={`admin-field-${k}`} value={editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} />
                </div>
              ))}
              <div>
                <label className="overline-label block mb-2">Collection</label>
                <select className="input-lux" data-testid="admin-field-collection" value={editing.collection || COLLECTIONS[0] || ""} onChange={(e) => setEditing({ ...editing, collection: e.target.value })}>
                  {COLLECTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="overline-label block mb-2">Colour Family</label>
                <select className="input-lux" data-testid="admin-field-family" value={editing.colour_family} onChange={(e) => setEditing({ ...editing, colour_family: e.target.value })}>
                  {FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="overline-label block mb-2">Fabric</label>
                <select className="input-lux" data-testid="admin-field-fabric" value={editing.fabric} onChange={(e) => setEditing({ ...editing, fabric: e.target.value })}>
                  {FABRICS.map((f) => <option key={f} value={f}>{f || "Fabric not assigned"}</option>)}
                </select>
              </div>
              <div>
                <label className="overline-label block mb-2">Price (₹)</label>
                <input type="number" min="0" className="input-lux" data-testid="admin-field-price" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
              </div>
              <div>
                <label className="overline-label block mb-2">Stock</label>
                <input type="number" min="0" className="input-lux" data-testid="admin-field-stock" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="overline-label block mb-2">Size / Length</label>
                <input className="input-lux" placeholder="e.g. 180 cm × 70 cm" data-testid="admin-field-length" value={editing.length} onChange={(e) => setEditing({ ...editing, length: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="overline-label block mb-2">Colour Description (shown to customers, e.g. "Dusty Pink")</label>
                <input className="input-lux" data-testid="admin-field-colour-desc" value={editing.colour_desc || ""} onChange={(e) => setEditing({ ...editing, colour_desc: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="overline-label block mb-2 text-rose">Supplier Note (PRIVATE — never shown on the website)</label>
                <textarea rows={2} className="input-lux resize-none" placeholder="e.g. supplier shade name, order reference…" data-testid="admin-field-supplier-note" value={editing.supplier_note || ""} onChange={(e) => setEditing({ ...editing, supplier_note: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="overline-label block mb-2">Description</label>
                <textarea rows={3} className="input-lux resize-none" data-testid="admin-field-description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="overline-label block mb-2">Care Instructions (optional — fabric default used if empty)</label>
                <textarea rows={2} className="input-lux resize-none" data-testid="admin-field-care" value={editing.care || ""} onChange={(e) => setEditing({ ...editing, care: e.target.value })} />
              </div>
              <div>
                <label className="overline-label block mb-2">Category</label>
                <select className="input-lux" data-testid="admin-field-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-[#2B211C]" data-testid="admin-field-featured" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />
                  <span className="text-sm">Featured on homepage</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-[#2B211C]" data-testid="admin-field-published" checked={editing.published !== false} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} />
                  <span className="text-sm">Visible on storefront</span>
                </label>
              </div>
            </div>

            {!editing.isNew && editing.type !== "bundle" && (
              <div className="mt-7 border-t border-beige pt-5">
                <p className="overline-label mb-2">Create Fabric Variant</p>
                <p className="text-[11px] text-taupe leading-relaxed mb-3">
                  Adds a separate product — {editing.shade} in another fabric. Same shade, family and price; the photo starts as a placeholder until you upload the real one.
                </p>
                <div className="flex gap-3">
                  <select
                    className="input-lux flex-1"
                    data-testid="admin-variant-fabric"
                    value={variantFabric}
                    onChange={(e) => setVariantFabric(e.target.value)}
                  >
                    {FABRICS.filter(Boolean).map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <button onClick={createVariant} disabled={busy} data-testid="admin-create-variant" className="btn-secondary !px-5 !py-3 shrink-0">
                    Create Variant
                  </button>
                </div>
              </div>
            )}

            <button onClick={save} disabled={busy} data-testid="admin-save-product" className="btn-primary w-full mt-7">
              {busy ? "Saving…" : editing.isNew ? "Create Product" : "Save Changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
